// src/storage/useLegalDocs.js
//
// Fetches the latest published version of each /legalDocuments type (one-shot
// getDocs; legal docs change rarely). Used by the /legal page (render content)
// and the checkout/signup consent gate (current versions -> acceptedLegalVersions
// + recordLegalConsent). Mock/no-db -> empty.
import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db, AUTH_MODE } from '../auth/firebase.js';

export const LEGAL_TYPE_LABELS = {
    terms_of_service: 'Terms of Service',
    privacy_policy: 'Privacy Policy',
    refund_policy: 'Refund Policy',
    ai_disclaimer: 'AI Study Partner Disclaimer'
};
export const LEGAL_TYPE_ORDER = [
    'terms_of_service', 'privacy_policy', 'refund_policy', 'ai_disclaimer'
];

function parseVer(v) {
    const m = /^v?(\d+)\.(\d+)/.exec(v || '');
    return m ? [Number(m[1]), Number(m[2])] : [0, 0];
}
// true if a is newer-or-equal to b
function newerOrEqual(a, b) {
    const A = parseVer(a), B = parseVer(b);
    return A[0] !== B[0] ? A[0] > B[0] : A[1] >= B[1];
}

export function useLegalDocs() {
    const [byType, setByType] = useState({}); // type -> {id, type, version, contentMarkdown, ...}
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Graceful degrade (P1): the load is one-shot, so a transient read failure used
    // to dead-end the consent gate until a full page reload. `reload()` re-runs the
    // fetch (the effect depends on `attempt`) so the UI can offer a Try-again.
    const [attempt, setAttempt] = useState(0);
    const reload = useCallback(() => setAttempt((n) => n + 1), []);

    useEffect(() => {
        let cancelled = false;
        if (AUTH_MODE === 'mock' || !db) {
            setByType({});
            setLoading(false);
            return undefined;
        }
        setLoading(true);
        setError(null); // a retry clears the previous failure
        getDocs(collection(db, 'legalDocuments'))
            .then((snap) => {
                if (cancelled) return;
                const latest = {};
                snap.forEach((d) => {
                    const data = d.data();
                    const t = data.type;
                    if (!t) return;
                    if (!latest[t] || newerOrEqual(data.version, latest[t].version)) {
                        latest[t] = { id: d.id, ...data };
                    }
                });
                setByType(latest);
                setLoading(false);
            })
            .catch((e) => {
                if (cancelled) return;
                console.error('useLegalDocs error:', e);
                setError(e);
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [attempt]);

    // type -> version, for acceptedLegalVersions / recordLegalConsent
    const versions = {};
    Object.values(byType).forEach((d) => { versions[d.type] = d.version; });

    return { byType, versions, loading, error, reload };
}

export default useLegalDocs;
